// GroundTruth QA pivots — two summary tables rendered between the issues
// bar and the placement grid on Sidekick's GT summary pane. Each pivot
// has its own rule for what counts as an issue. Pure data shapers; the
// rendering lives in ./GtQaPivotPanel.js.

// Pivot 1 — Location filters · ZIP code assignment.
// One row per ad group. Columns: zip count + total filter count from the
// matching location_lists entry, plus a Missing-filter flag when the
// ad group declares a location_filter that doesn't resolve to a list.
export function buildLocationFilterPivot(ctsReview) {
  const r = ctsReview?.result || {};
  const adgroups = Array.isArray(r.adgroups) ? r.adgroups : [];
  const lists = Array.isArray(r.location_lists) ? r.location_lists : [];
  // Index location_lists by filter_name (case-insensitive). Each value:
  // { totalCount, zipCount } so we don't re-scan the lists per ad group.
  const byFilter = new Map();
  for (const ll of lists) {
    const key = String(ll?.filter_name || '').trim().toLowerCase();
    if (!key) continue;
    const locs = Array.isArray(ll?.location_list?.locations) ? ll.location_list.locations : [];
    const zipSet = new Set();
    for (const loc of locs) {
      const z = String(loc?.zip || '').trim();
      if (z) zipSet.add(z);
    }
    byFilter.set(key, { totalCount: locs.length, zipCount: zipSet.size });
  }
  const rows = adgroups.map((ag, idx) => {
    const filter = String(ag?.location_filter || '').trim();
    const lookup = filter ? byFilter.get(filter.toLowerCase()) : null;
    const hasFilter = Boolean(filter);
    const matched = Boolean(lookup);
    return {
      rowId: `gt-locpivot-${idx}`,
      ad_group_name: ag?.ad_group_name || '—',
      location_filter: filter || '—',
      zipCount: lookup?.zipCount ?? 0,
      totalCount: lookup?.totalCount ?? 0,
      status: !hasFilter ? 'missing' : (!matched ? 'no_match' : 'ok'),
    };
  });
  const issueCount = rows.filter((r) => r.status !== 'ok').length;
  return { rows, issueCount, hasLists: lists.length > 0 };
}

// Pivot 2 — DCM placements per ad group.
// Columns are discovered from any populated placement_id_* field on any
// ad group (e.g. placement_id_300x250 → "300x250"). Per row: count of
// non-empty placement IDs per size, total, missing-vs-creative_sizes,
// duplicates-across-the-plan. Renders even when empty (per UX call).
export function buildDcmPlacementPivot(ctsReview) {
  const r = ctsReview?.result || {};
  const adgroups = Array.isArray(r.adgroups) ? r.adgroups : [];
  // Discover sizes present on any ad group. Falls back to the three
  // mobile sizes shown in the wireframe so the table renders cleanly
  // even when no ad group has populated placement_id_* yet.
  const FALLBACK_SIZES = ['300x250', '320x50', '320x480'];
  const sizeSet = new Set();
  for (const ag of adgroups) {
    for (const k of Object.keys(ag || {})) {
      const m = /^placement_id_(\d+x\d+)$/i.exec(k);
      if (m && ag[k] != null && String(ag[k]).trim() !== '') sizeSet.add(m[1].toLowerCase());
    }
  }
  const sizeKeys = sizeSet.size > 0
    ? Array.from(sizeSet).sort()
    : FALLBACK_SIZES;
  // First pass — count per size per row.
  const rowsRaw = adgroups.map((ag, idx) => {
    const counts = {};
    const placementIds = {}; // size → id (for dedup detection)
    let total = 0;
    for (const sz of sizeKeys) {
      const v = ag?.[`placement_id_${sz}`];
      const has = v != null && String(v).trim() !== '';
      counts[sz] = has ? 1 : 0;
      total += counts[sz];
      if (has) placementIds[sz] = String(v).trim();
    }
    // Declared sizes from creative_sizes (e.g. " 320x50, 300x250" →
    // ["320x50","300x250"]) so we can flag a size declared but
    // un-placement-id'd.
    const declared = String(ag?.creative_sizes || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const missingSizes = declared.filter((sz) => sizeKeys.includes(sz) && counts[sz] === 0);
    return {
      rowId: `gt-dcmpivot-${idx}`,
      ad_group_name: ag?.ad_group_name || '—',
      counts,
      total,
      placementIds,
      missingSizes,
    };
  });
  // Second pass — duplicates across ad groups: same placement_id used by
  // another ad group for the same size. Counted per row.
  const usage = new Map(); // `${size}|${id}` → count
  for (const row of rowsRaw) {
    for (const sz of sizeKeys) {
      const id = row.placementIds[sz];
      if (!id) continue;
      const k = `${sz}|${id}`;
      usage.set(k, (usage.get(k) || 0) + 1);
    }
  }
  const rows = rowsRaw.map((row) => {
    let dups = 0;
    for (const sz of sizeKeys) {
      const id = row.placementIds[sz];
      if (id && (usage.get(`${sz}|${id}`) || 0) > 1) dups += 1;
    }
    return { ...row, duplicates: dups };
  });
  const issueCount = rows.filter((r) => r.missingSizes.length > 0 || r.duplicates > 0).length;
  const anyPopulated = rows.some((r) => r.total > 0);
  return { rows, sizeKeys, issueCount, anyPopulated };
}
